/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import * as amplitude from '@amplitude/analytics-browser';
import { useLazyQuery, useQuery } from '@apollo/client';
import { isNil } from 'es-toolkit';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Outlet, useParams } from 'react-router-dom';

import {
  IntegrationsContextDocument,
  type IntegrationsContextQuery,
} from '@fai/friendli-suite/generated/client/graphql';

import { parseAmpPropertyEntries, useAmpStore } from './amplitude';
import { parseLDContext } from './launch-darkly';

/**
 * @url https://docs.launchdarkly.com/sdk/client-side/react/react-web#identify-the-context
 * @url https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#user-properties
 * @todo update fetchPolicy when stabilized
 */
export function SetIntegrationsContext() {
  const { ampIdentify, identify } = useAmpStore();
  const ldClient = useLDClient();
  const { teamId } = useParams();

  useQuery(IntegrationsContextDocument, {
    variables: { teamId: teamId ?? '', skipTeam: isNil(teamId) },
    skip: isNil(ldClient),
    fetchPolicy: 'no-cache',
    onCompleted: (data) => cb({ data, ldClient, ampIdentify, identify }),
  });

  return <Outlet />;
}

/**
 * @url https://docs.launchdarkly.com/sdk/client-side/react/react-web#identify-the-context
 * @url https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#user-properties
 * @todo update fetchPolicy when stabilized
 */
export const useIntegrationsContextLazyQuery = () => {
  const { ampIdentify, identify } = useAmpStore();
  const ldClient = useLDClient();
  const { teamId } = useParams();

  const [query] = useLazyQuery(IntegrationsContextDocument, {
    variables: { teamId: teamId ?? '', skipTeam: isNil(teamId) },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => cb({ data, ldClient, ampIdentify, identify }),
  });

  return { query };
};

const cb = ({
  data,
  ldClient,
  ampIdentify,
  identify,
}: {
  data: IntegrationsContextQuery;
} & { ldClient: ReturnType<typeof useLDClient> } & ReturnType<typeof useAmpStore>) => {
  // update ldClient context
  ldClient?.identify(parseLDContext(data));

  // update amplitude user properties
  parseAmpPropertyEntries(data).forEach(([key, value]) => {
    if (key === 'user_id' && typeof value === 'string') {
      amplitude.setUserId(value);
    }

    ampIdentify.set(key, value);
  });

  identify();
};
